import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../common/AuthContext";
import BrokerLogo from "./BrokerLogo";
import BrokerPageHeader from "./BrokerPageHeader";
import { deleteBrokerAccount, getBrokerAccount, getBrokerId } from "../../../service/api/BrokerAccountApi";
import "./Brokers.css";

const maskValue = (value = "") =>
  value.length > 4 ? `${value.slice(0, 4)}${"*".repeat(Math.min(value.length - 4, 8))}` : value || "—";

const isPendingVerification = (broker) =>
  !broker?.status || broker.status === "PENDING_VERIFICATION";

const getBrokerType = (broker) =>
  broker.brokerType === "DHAN" ? "dhan" : broker.brokerType === "FYERS" ? "fyers" : "angel";

const getBrokerName = (broker) =>
  broker.brokerType === "DHAN" ? "Dhan" : broker.brokerType === "FYERS" ? "Fyers" : "Angel One";

const getBrokerEditPath = (brokerType) => {
  if (brokerType === "DHAN") return "/brokers/add/dhan";
  if (brokerType === "FYERS") return "/brokers/add/fyers";
  if (["ANGEL", "ANGELONE", "ANGEL_ONE"].includes(brokerType)) return "/brokers/add/angel";
  return null;
};

const BrokerDetails = () => {
  const navigate = useNavigate();
  const { brokerId } = useParams();
  const { user } = useAuth();
  const [broker, setBroker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let active = true;
    const loadBroker = async () => {
      try {
        if (!user?.userId) throw new Error("Your user session is missing. Please log in again.");
        const data = await getBrokerAccount(brokerId, user.userId);
        if (active) setBroker(data);
      } catch (error) {
        if (active) setLoadError(error.response?.data?.message || error.message || "Unable to load broker.");
      } finally {
        if (active) setLoading(false);
      }
    };
    loadBroker();
    return () => { active = false; };
  }, [brokerId, user?.userId]);

  const deleteBroker = async () => {
    if (!window.confirm(`Remove ${broker.accountName}?`)) return;
    try {
      await deleteBrokerAccount(getBrokerId(broker), user.userId);
      navigate("/brokers");
    } catch (error) {
      setLoadError(error.response?.data?.message || error.message || "Unable to remove broker.");
    }
  };

  if (loading) {
    return <main className="broker-page"><div className="broker-empty"><i className="fa fa-spinner fa-spin fa-2x"></i><p>Loading broker details...</p></div></main>;
  }
  if (!broker && !loadError) return <Navigate to="/brokers" replace />;
  if (loadError) return <main className="broker-page"><div className="broker-empty"><div className="broker-api-error">{loadError}</div></div></main>;

  const brokerEditPath = getBrokerEditPath(broker.brokerType);

  return (
    <main className="broker-page">
      <BrokerPageHeader title="Broker Details" backTo="/brokers" />

      <section className="broker-details-card">
        <div className="broker-details-main">
          <div className="broker-details-title">
            <BrokerLogo type={getBrokerType(broker)} />
            <div>
              <h1>{broker.accountName}</h1>
              <span className="broker-status">{broker.status || (broker.active ? "Active" : "Inactive")}</span>
            </div>
          </div>

          <dl className="broker-details-list">
            <div><dt>Account Name</dt><dd>{broker.accountName}</dd></div>
            <div><dt>Broker</dt><dd>{getBrokerName(broker)}</dd></div>
            <div><dt>Client ID</dt><dd>{maskValue(broker.clientId || broker.clientCode)}</dd></div>
            {broker.apiKey && <div><dt>API Key</dt><dd>{maskValue(broker.apiKey)}</dd></div>}
            <div><dt>Connected On</dt><dd>{broker.createdAt ? new Date(broker.createdAt).toLocaleString() : "—"}</dd></div>
            <div><dt>Status</dt><dd><span className="broker-status">{broker.status || (broker.active ? "Active" : "Inactive")}</span></dd></div>
          </dl>
        </div>

        <aside className="broker-details-actions">
          <h2>Actions</h2>
          <button onClick={() => navigate(
            brokerEditPath,
            { state: { broker } }
          )} disabled={!brokerEditPath}>
            <i className="fa fa-refresh" aria-hidden="true"></i>
            {isPendingVerification(broker) ? "Connect & Verify" : "Update Broker"}
          </button>
          <button className="broker-details-actions__danger" onClick={deleteBroker}>
            <i className="fa fa-trash" aria-hidden="true"></i>
            Remove Broker
          </button>
        </aside>
      </section>
    </main>
  );
};

export default BrokerDetails;
